// Corporate Gifts landing — client behaviour for the "Products Gift Edit"
// and "Corporate Inquiry" modules:
// - classifies each gift card (brand / category / numeric price) from data
//   attributes so the brand cards, category pills and budget select can
//   filter the grid without any backend round trip
// - keeps the "Add to inquiry" selection in localStorage, mirrors it into
//   the inquiry module's "Your gift ideas" panel and badges the sticky
//   subnav's "Your inquiry" link
// - composes the corporate-only fields + selected gifts into the hidden
//   `message` field before the shared js-form handler posts to
//   api.forms.contact (schema only accepts the standard contact fields)

import { buyButtonClickHandler } from './global-cart';

const STORAGE_KEY = 'raffiCorporateGiftInquiry';

interface SelectedGift {
  id: string;
  label: string;
  url: string;
}

function loadSelection(): SelectedGift[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveSelection(gifts: SelectedGift[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(gifts));
  } catch {
    // Storage unavailable (private mode) — selection lives for the page view only
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const edit = document.querySelector<HTMLElement>('.js-cg-edit');
  const inquiry = document.querySelector<HTMLElement>('.js-cg-inquiry');
  if (!edit && !inquiry) return;

  let selection = loadSelection();

  // ------------------------------------------------------------ gift edit

  const cards = edit ? Array.from(edit.querySelectorAll<HTMLElement>('.js-cg-card')) : [];

  // Brand comes from the product's brand tag when present, else from the
  // product title (the curated set always names the maison first)
  const brandCards = edit ? Array.from(edit.querySelectorAll<HTMLElement>('.js-cg-brand')) : [];
  const brandNames = brandCards.map((button) => button.dataset.cgBrand || '');

  function resolveBrand(card: HTMLElement): string {
    const source = `${card.dataset.cgBrandTag || ''} ${card.dataset.cgFullTitle || ''}`;
    const match = brandNames.find((name) => name && new RegExp(name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i').test(source));
    return match || card.dataset.cgBrandTag || '';
  }

  // Category buckets mirror the design's gift-edit pills. Order matters:
  // specific writing-instrument words win before the leather-goods words so
  // "rollerball pen & card holder set" lands under writing instruments while
  // "single pen pouch" stays under leather.
  function resolveCategory(card: HTMLElement, brand: string): string {
    if (/tudor|omega/i.test(brand)) return 'timepieces';
    const title = card.dataset.cgFullTitle || '';
    if (/rollerball|ballpoint|fineliner|fountain/i.test(title)) return 'writing';
    if (/wallet|holder|case|pouch|notebook|leather/i.test(title)) return 'leather';
    return 'accessories';
  }

  cards.forEach((card) => {
    const brand = resolveBrand(card);
    card.dataset.cgBrand = brand;
    card.dataset.cgCategory = resolveCategory(card, brand);
    card.dataset.cgPriceNum = String(parseFloat((card.dataset.cgPrice || '').replace(/[^0-9.]/g, '')) || 0);
    // Fill the card's brand eyebrow when the product had no brand tag
    const brandSlot = card.querySelector<HTMLElement>('.js-cg-card-brand');
    if (brandSlot && !brandSlot.textContent?.trim()) brandSlot.textContent = brand;
  });

  // Brand-card counts
  brandCards.forEach((button) => {
    const name = button.dataset.cgBrand || '';
    const count = cards.filter((card) => card.dataset.cgBrand === name).length;
    const slot = button.querySelector<HTMLElement>('.js-cg-brand-count');
    const template = (count === 1 ? button.dataset.countOne : button.dataset.countMany) || '%count%';
    if (slot) slot.textContent = template.replace('%count%', String(count));
  });

  // Merchandising: sellable gifts (in stock + available:online) lead the
  // grid, out-of-stock ones sink to the end — stable within each group
  if (cards.length) {
    const grid = cards[0].parentElement;
    if (grid) {
      const sellable = cards.filter((card) => card.dataset.cgSellable === '1');
      const unavailable = cards.filter((card) => card.dataset.cgSellable !== '1');
      if (unavailable.length && sellable.length) {
        [...sellable, ...unavailable].forEach((card) => grid.appendChild(card));
      }
    }
  }

  const state = { brand: 'all', category: 'all', budget: 'all' };
  const budgetSelect = edit?.querySelector<HTMLSelectElement>('.js-cg-budget') || null;
  const results = edit?.querySelector<HTMLElement>('.js-cg-results') || null;
  const emptyBox = edit?.querySelector<HTMLElement>('.js-cg-empty') || null;
  const grid = edit?.querySelector<HTMLElement>('#cg-gift-grid') || null;
  const clearButtons = edit ? Array.from(edit.querySelectorAll<HTMLElement>('.js-cg-clear')) : [];
  const allBrandsButton = edit?.querySelector<HTMLElement>('.js-cg-all-brands') || null;
  const categoryButtons = edit ? Array.from(edit.querySelectorAll<HTMLElement>('[data-cg-category]')).filter((el) => el.tagName === 'BUTTON') : [];

  function budgetMatches(card: HTMLElement): boolean {
    if (state.budget === 'all') return true;
    const [min, max] = state.budget.split('-');
    const price = parseFloat(card.dataset.cgPriceNum || '0');
    if (min && price < parseFloat(min)) return false;
    if (max && price >= parseFloat(max)) return false;
    return true;
  }

  function applyFilters(): void {
    if (!edit) return;
    let shown = 0;
    cards.forEach((card) => {
      const visible =
        (state.brand === 'all' || card.dataset.cgBrand === state.brand) &&
        (state.category === 'all' || card.dataset.cgCategory === state.category) &&
        budgetMatches(card);
      card.hidden = !visible;
      if (visible) shown += 1;
    });

    if (grid) grid.hidden = shown === 0;
    if (emptyBox) emptyBox.hidden = shown !== 0;

    if (results) {
      const total = cards.length;
      const template =
        shown === 0
          ? results.dataset.labelNone
          : shown === total
            ? results.dataset.labelAll
            : results.dataset.labelSome;
      results.textContent = (template || '')
        .replace('%shown%', String(shown))
        .replace('%total%', String(total));
    }

    const filtered = state.brand !== 'all' || state.category !== 'all' || state.budget !== 'all';
    clearButtons.forEach((button) => {
      if (button.closest('.js-cg-empty')) return; // always visible inside the empty state
      button.hidden = !filtered;
    });

    categoryButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.cgCategory === state.category)));
    brandCards.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.cgBrand === state.brand)));
    allBrandsButton?.setAttribute('aria-pressed', String(state.brand === 'all'));
  }

  categoryButtons.forEach((button) =>
    button.addEventListener('click', () => {
      state.category = button.dataset.cgCategory || 'all';
      applyFilters();
    })
  );
  brandCards.forEach((button) =>
    button.addEventListener('click', () => {
      state.brand = state.brand === button.dataset.cgBrand ? 'all' : button.dataset.cgBrand || 'all';
      state.category = 'all';
      applyFilters();
    })
  );
  allBrandsButton?.addEventListener('click', () => {
    state.brand = 'all';
    applyFilters();
  });
  budgetSelect?.addEventListener('change', () => {
    state.budget = budgetSelect.value;
    applyFilters();
  });
  clearButtons.forEach((button) =>
    button.addEventListener('click', () => {
      state.brand = 'all';
      state.category = 'all';
      state.budget = 'all';
      if (budgetSelect) budgetSelect.value = 'all';
      applyFilters();
    })
  );

  // Quick add-to-bag on the gift cards — the global cart's delegated
  // handler only covers collection/search containers, so bind our own
  edit?.addEventListener('click', async (event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('.js-add-to-cart');
    if (!button || !button.dataset.id) return;
    event.preventDefault();
    button.classList.add('processing');
    try {
      await buyButtonClickHandler(button.dataset.id, []);
    } finally {
      button.classList.remove('processing');
    }
  });

  // ------------------------------------------------------------ selection

  const toast = document.querySelector<HTMLElement>('.js-cg-toast');
  let toastTimer: number | undefined;

  function announce(message: string): void {
    if (!toast) return;
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    toastTimer = window.setTimeout(() => {
      toast.hidden = true;
    }, 2800);
  }

  function renderSelection(): void {
    // Badge on the sticky subnav chip (and any other #your-inquiry nav link)
    document.querySelectorAll<HTMLAnchorElement>('.fc-brand-subnav a[href="#your-inquiry"]').forEach((link) => {
      let badge = link.querySelector<HTMLElement>('.cg-count');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'cg-count';
        link.appendChild(badge);
      }
      badge.textContent = String(selection.length);
      badge.hidden = selection.length === 0;
    });

    // Inquiry panel list
    const list = inquiry?.querySelector<HTMLElement>('.js-cg-selected');
    const empty = inquiry?.querySelector<HTMLElement>('.js-cg-selection-empty');
    if (empty) empty.hidden = selection.length > 0;
    if (list) {
      list.replaceChildren();
      selection.forEach((gift) => {
        const item = document.createElement('li');
        item.className = 'cg-inquiry__sel-item';
        const label = document.createElement('span');
        label.textContent = gift.label;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'cg-inquiry__remove';
        remove.dataset.cgRemove = gift.id;
        remove.textContent = list.dataset.removeLabel || 'Remove';
        remove.setAttribute('aria-label', `${remove.textContent} — ${gift.label}`);
        item.append(label, remove);
        list.append(item);
      });
    }

    // Card button states
    document.querySelectorAll<HTMLElement>('.js-cg-add').forEach((button) => {
      const added = selection.some((gift) => gift.id === button.dataset.cgAdd);
      button.setAttribute('aria-pressed', String(added));
      button.textContent = (added ? button.dataset.labelAdded : button.dataset.labelAdd) || button.textContent;
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const add = target.closest<HTMLElement>('[data-cg-add]');
    const remove = target.closest<HTMLElement>('[data-cg-remove]');
    if (!add && !remove) return;

    const id = add ? add.dataset.cgAdd : remove?.dataset.cgRemove;
    if (!id) return;

    if (selection.some((gift) => gift.id === id)) {
      selection = selection.filter((gift) => gift.id !== id);
      announce(toast?.dataset.msgRemoved || 'Gift removed from your inquiry.');
    } else {
      const card = document.querySelector<HTMLElement>(`.js-cg-card[data-cg-gift="${id}"]`);
      if (!card) return;
      selection.push({
        id,
        label: `${card.dataset.cgBrand || ''} · ${card.dataset.cgName || ''}${card.dataset.cgPrice ? ` (${card.dataset.cgPrice} CAD)` : ''}`,
        url: card.dataset.cgUrl || '',
      });
      announce(toast?.dataset.msgAdded || 'Gift added. Review it in Your inquiry.');
    }
    saveSelection(selection);
    renderSelection();
  });

  // -------------------------------------------------------------- inquiry

  const form = inquiry?.querySelector<HTMLFormElement>('form');

  // Prefill helpers: occasion links ("Plan employee gifts" columns) and the
  // personalization split feature's CTA both point at #your-inquiry
  // Maps the occasion-column CTA label to the option INDEX in the occasion
  // select (1 = employee, 2 = client, 3 = milestones), so it holds in both
  // locales without string matching against translated option values
  const occasionMap: Record<string, number> = {
    'plan employee gifts': 1,
    'plan client gifts': 2,
    'plan milestone gifts': 3,
    'planifier des cadeaux pour employés': 1,
    'planifier des cadeaux clients': 2,
    'planifier des cadeaux jalons': 3,
  };

  document.addEventListener('click', (event) => {
    const link = (event.target as HTMLElement).closest<HTMLAnchorElement>('a[href="#your-inquiry"]');
    if (!link || !form) return;
    const occasionIndex = occasionMap[(link.textContent || '').trim().toLowerCase()];
    const occasionSelect = form.querySelector<HTMLSelectElement>('.js-cg-occasion');
    if (occasionIndex && occasionSelect && occasionSelect.options.length > occasionIndex) {
      occasionSelect.selectedIndex = occasionIndex;
    }
    if (link.closest('.fc-split-feature')) {
      const personalization = form.querySelector<HTMLSelectElement>('.js-cg-personalization');
      if (personalization && personalization.options.length > 1) personalization.selectedIndex = 1; // "Interested in engraving"
    }
  });

  if (form) {
    const dateInput = form.querySelector<HTMLInputElement>('.js-cg-date');
    if (dateInput) {
      const now = new Date();
      dateInput.min = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('-');
    }

    // Compose the hidden `message` before the shared js-form handler builds
    // its FormData. Document-level capture runs ahead of the form's own
    // submit listeners regardless of registration order.
    document.addEventListener(
      'submit',
      (event) => {
        if (event.target !== form) return;
        const value = (name: string): string => {
          const field = form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
          return field ? field.value.trim() : '';
        };
        const gifts = selection.length
          ? selection.map((gift) => `- ${gift.label}${gift.url ? ` — ${location.origin}${gift.url}` : ''}`).join('\n')
          : 'Please help me choose.';
        const lines = [
          'Corporate gift inquiry',
          '',
          `Company: ${value('company')}`,
          `Occasion: ${value('occasion')}`,
          `Number of gifts: ${value('quantity')}`,
          `Budget per gift (CAD): ${value('budgetPerGift')}`,
          `Desired gift date: ${value('desiredDate') || 'Flexible / not specified'}`,
          `Personalization: ${value('personalization')}`,
          '',
          'Selected gift ideas:',
          gifts,
        ];
        const notes = value('notes');
        if (notes) lines.push('', `Notes: ${notes}`);
        const message = form.querySelector<HTMLInputElement>('.js-cg-message');
        if (message) message.value = lines.join('\n');
      },
      true
    );
  }

  renderSelection();
  applyFilters();
});
